import type { CSSProperties } from "react";
import { fillsMainArea } from "@/app/navigation/full-screen-routes";
import { chatLauncherVisible } from "@/features/chat/lib/chat-launcher-visibility";
import type { PermissionSet } from "@/lib/permissions/permissions";

/**
 * Sohbet düğmesinin kapladığı alan, uygulamanın KAYAN öğesinde — telefonun alt güvenli alanı dahil.
 *
 * `--launcher-reserve` kitin jetonu: düğmenin boyu + iki kenar boşluğu (WCAG 2.2 SC 2.4.11).
 */
export const LAUNCHER_RESERVE = "calc(var(--launcher-reserve) + env(safe-area-inset-bottom, 0px))";

/**
 * `<main>`'in alt rezervi — yalnız köşede sohbet düğmesi GERÇEKTEN varken (denetim Sohbet 21).
 *
 * NEDEN. Düğme `position: fixed` ile sağ altta duruyor ve sayfanın alt kenarındaki kontrolün
 * üstüne biniyordu (Dosya Dönüştürücü'nün sayfalama seçicisi). Kit bunun için
 * `html { scroll-padding-block-end }` yazıyor — ama bu uygulamada kayan öğe `html` değil `<main>`,
 * yani kitin rezervi hiçbir şeyi korumuyordu. `<main>`'in yalnız `pb-24`'ü vardı: sayfanın SONU
 * düğmenin üstüne kayabiliyordu, ama Tab ile odaklanan bir kontrol tarayıcı tarafından tam alt
 * kenara kaydırılıyor ve düğmenin altında kalıyordu.
 *
 * İKİ ÖZELLİK, İKİ İŞ:
 * - `scroll-padding-block-end`: odaklanan ya da `scrollIntoView` edilen kontrol düğmenin ÜSTÜNDE
 *   duruyor;
 * - `padding-block-end`: sayfanın son satırı düğmenin üstüne kadar kaydırılabiliyor.
 * İkisi de aynı jetondan: düğmenin ölçüsü kitte değişirse rezerv onunla değişir.
 *
 * DÜĞME YOKSA REZERV YOK. Sohbet sayfasında (düğme orada çizilmiyor) ya da sohbet kapalıyken
 * 96 piksellik boşluk yalnız yer kaplıyordu — ve sohbet sayfasında daha kötüsü: 375 px'lik bir
 * telefonda (17 Eylül 2026 ölçümü) sayfa 93 piksel taşıyor, yazma kutusu kıvrımın altında
 * kalıyordu.
 *
 * Kit CSS'i EZİLMİYOR: kitin sınıflarına dokunan bir seçici yok, yalnız uygulamanın kendi
 * kaydırıcısına kitin jetonu veriliyor.
 */
export function mainScrollerStyle(launcherShown: boolean): CSSProperties | undefined {
  if (!launcherShown) return undefined;
  return { paddingBlockEnd: LAUNCHER_RESERVE, scrollPaddingBlockEnd: LAUNCHER_RESERVE };
}

/**
 * `<main>` sohbet düğmesinin rezervini taşıyor mu — `MainScroller` ve önizleme tezgahı AYNI kuralı
 * okuyor; tezgah bunu taşımadığı için Notlarım'ın alt şeridi tezgahta hiç görünmemişti.
 *
 * `<main>`'İ TAMAMEN DOLDURAN EKRANLARDA (`fillsMainArea`, `full-screen-routes.ts`) HAYIR: Notlarım
 * ve belge düzenleyici `<main>`'in yüksekliğinin tamamını `100%` ile alıyor — kabuk `h-dvh` bir
 * sütun, `<main>` onun esnek satırı, yani `100%` = ekran − kabuğun GERÇEK üst şeritleri; sihirli
 * sayı yok — ve kendi içlerinde kayıyor. Orada `<main>`'in alt rezervi hiçbir kontrolü korumuyor,
 * yalnız altta 88 piksellik bir zemin şeridi bırakıyordu (Notlarım, 18 Eylül 2026). Rezerv o
 * ekranın iç kaydırıcısında: kitin `data-launcher-reserve="pad"` özniteliği.
 */
export function mainAreaReserve(input: {
  permissions: Pick<PermissionSet, "can" | "isModuleEnabled">;
  pathname: string;
}): boolean {
  return chatLauncherVisible(input) && !fillsMainArea(input.pathname);
}
